import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import MarketingLayout from './components/MarketingLayout'

// ─── Animation variants ───────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

const heroFadeUp = {
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

const heroStaggerContainer = {
  visible: { transition: { staggerChildren: 0.08 } },
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const PlumbingIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0H5a2 2 0 00-2 2v2a2 2 0 002 2h2m0-6h2a2 2 0 012 2v2a2 2 0 01-2 2H7M17 8V4m0 0h-2a2 2 0 00-2 2v2a2 2 0 002 2h2m0-6h2a2 2 0 012 2v2a2 2 0 01-2 2h-2m0 4v8" />
  </svg>
)
const ElectricalIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
)
const HVACIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
  </svg>
)

// ─── Stats ────────────────────────────────────────────────────────────────────
const stats = [
  { value: '3', label: 'Trades at launch — Plumbing, Electrical, HVAC' },
  { value: '$150', label: 'Flat trip charge' },
  { value: '$0', label: 'What contractors pay us' },
  { value: '10', label: 'Launch metros' },
]

// ─── Services ─────────────────────────────────────────────────────────────────
const services = [
  {
    icon: <PlumbingIcon />,
    title: 'Plumbing',
    description: 'Burst pipes, leaks, drain clogs, water heater failures - dispatched to a licensed plumber in minutes.',
    color: 'bg-blue-50 text-blue-600',
  },
  {
    icon: <ElectricalIcon />,
    title: 'Electrical',
    description: 'Outages, tripped breakers, faulty wiring, panel issues - connected to a certified electrician fast.',
    color: 'bg-yellow-50 text-yellow-600',
  },
  {
    icon: <HVACIcon />,
    title: 'HVAC',
    description: 'AC failures, heating breakdowns, ventilation issues - reach an HVAC specialist when you need it most.',
    color: 'bg-green-50 text-green-600',
  },
]

// ─── How it works steps ───────────────────────────────────────────────────────
const steps = [
  {
    step: '01',
    title: 'Text SOS SMS',
    description: 'Send a simple SMS describing your issue. Our system guides you through the process without an app or account.',
  },
  {
    step: '02',
    title: 'We Dispatch Instantly',
    description: 'SOS SMS matches you with the top 3 licensed providers near your location. You select the one with the best ETA.',
  },
  {
    step: '03',
    title: 'Help Arrives Fast',
    description: 'Your chosen provider heads to you immediately. Track their arrival and rate your experience - all through SMS.',
  },
]

// ─── Features ─────────────────────────────────────────────────────────────────
const features = [
  {
    icon: '📱',
    title: 'No App Required',
    description: 'Works entirely via SMS. Any phone can use SOS SMS - smartphones and basic phones alike.',
  },
  {
    icon: '🔒',
    title: 'Verified Providers',
    description: 'Every provider is license-verified and insurance-verified for their trade in their state, before they receive a single request.',
  },
  {
    icon: '⚡',
    title: 'Real-Time Dispatch',
    description: 'Our engine instantly finds the closest available professionals and fires off simultaneous notifications.',
  },
  {
    icon: '💳',
    title: 'Transparent Pricing',
    description: '$150 flat trip charge when we dispatch, credited towards the work completed. Membership is $14/month or $99/year. Work beyond the visit is billed directly by your contractor — we take no cut.',
  },
  // {
  //   icon: '⭐',
  //   title: 'Rated & Reviewed',
  //   description: 'After every job, customers rate their provider. Ratings are displayed publicly to ensure quality.',
  // },
  {
    icon: '🗺️',
    title: 'Location-Aware',
    description: 'ZIP code and county-based matching ensures you always get the closest provider to your location.',
  },
]



// ─── FAQ ──────────────────────────────────────────────────────────────────────
const faqs = [
  {
    q: 'How does SOS SMS work?',
    a: 'Simply text your home service need to our number. Our system will guide you through describing the issue and your location, then provide the nearest available provider to you and ETA for you to choose from.',
  },
  {
    q: 'What services does SOS SMS cover?',
    a: 'SOS SMS currently covers Plumbing, Electrical, and HVAC (heating, ventilation, and air conditioning) services. We are continuously expanding to new service categories.',
  },
  {
    q: 'How much does it cost?',
    a: 'Membership is $14/month or $99/year. A $150 flat trip charge is captured when we dispatch, credited towards the work completed by the service provider.',
  },
  {
    q: 'Are providers licensed and insured?',
    a: 'Yes. All providers on the SOS SMS platform are required to submit valid license and insurance documents. These are reviewed and approved by our team before any provider can accept jobs.',
  },
  {
    q: 'How do I become a service provider on SOS SMS?',
    a: 'Visit our Provider Sign Up page, complete your business profile, upload your license and insurance documents, and connect your Stripe account for payouts. Our team reviews your application promptly.',
  },
]

// ─── FAQ Item ─────────────────────────────────────────────────────────────────
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 pr-4">{q}</span>
        <span className={`flex-shrink-0 w-6 h-6 rounded-full border-2 border-primary text-primary flex items-center justify-center transition-transform duration-200 ${open ? 'rotate-45' : ''}`}>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-100">
          <p className="pt-4">{a}</p>
        </div>
      )}
    </div>
  )
}



// ─── Main Component ───────────────────────────────────────────────────────────
function HomePage() {
  useEffect(() => {
    document.title = 'SOS SMS — One text. Help on the way.'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      "Water heater dead at 11pm? Furnace out in January? Text one number. We find a licensed, insured contractor near you and get them moving — no app, no account, no calling down a list of people who don't pick up."
    )
  }, [])

  return (
    <MarketingLayout>

      {/* ── HERO ──────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text */}
            <motion.div initial="visible" animate="visible" variants={heroStaggerContainer}>
              <motion.div variants={heroFadeUp} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse"></span>
                SMS-Based Dispatch — Plumbing, Electrical, HVAC
              </motion.div>

              <motion.h1 variants={heroFadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                One text.
                <br />
                <span className="text-green-300">Help on the way.</span>
              </motion.h1>

              <motion.p variants={heroFadeUp} className="text-lg text-green-100 leading-relaxed mb-8 max-w-lg">
                Water heater dead at 11pm? Furnace out in January? Text one number. We find a licensed, insured contractor near you and get them moving — no app, no account, no calling down a list of people who don't pick up.
              </motion.p>

              <motion.div variants={heroFadeUp} className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/waitlist"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-green-800 font-bold rounded-2xl hover:bg-green-50 transition-colors shadow-lg text-base"
                >
                  Join the founding member waitlist
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right: SMS mockup */}
            <motion.div
              initial={{ opacity: 1, x: 0 }}
              animate={{ opacity: 1, x: 0, transition: { duration: 0.35, ease: 'easeOut' } }}
              className="hidden lg:flex justify-center"
            >
              <div className="relative">
                <div className="w-72 bg-white rounded-3xl shadow-2xl p-1 border border-white/20">
                  {/* Phone top bar */}
                  <div className="bg-gray-900 rounded-t-[22px] px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-white text-xs font-semibold">SOS SMS</div>
                        <div className="text-green-400 text-xs inline-flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
                          </svg>
                          Online
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Chat messages */}
                  <div className="bg-gray-50 px-4 py-4 space-y-3 min-h-[320px]">
                    {[
                      { from: 'system', text: 'Hello! Welcome to SOS SMS. We offer Plumbing, Electrical, and HVAC services. What do you need?' },
                      { from: 'user', text: 'My pipe is leaking badly!' },
                      { from: 'system', text: 'Got it - Plumbing emergency. What\'s your address or ZIP code?' },
                      { from: 'user', text: '33101' },
                      { from: 'system', text: '✅ Found 3 nearby providers. The closest can arrive in 18 min. Reply 1 to confirm.' },
                      { from: 'user', text: '1' },
                      { from: 'system', text: '🔧 Your provider is on the way! ETA 18 min. A $150 trip charge will be authorized.' },
                    ].map((msg, i) => (
                      <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${
                            msg.from === 'user'
                              ? 'bg-primary text-white rounded-br-sm'
                              : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-sm'
                          }`}
                        >
                          {msg.text}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-b-[22px] px-3 py-3 flex items-center gap-2 border-t border-gray-100">
                    <input
                      readOnly
                      placeholder="Type a message..."
                      className="flex-1 text-xs bg-gray-100 rounded-full px-4 py-2 text-gray-500 outline-none"
                    />
                    <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat) => (
              <motion.div key={stat.label} variants={fadeUp} className="text-center">
                <div className="text-4xl lg:text-5xl font-extrabold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-gray-500 font-medium break-words">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── SERVICES ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              What We Cover
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Emergency Services, On Demand
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
              From burst pipes to power outages, SOS SMS dispatches certified professionals directly to your door - fast.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {services.map((service) => (
              <motion.div
                key={service.title}
                variants={fadeUp}
                className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className={`w-16 h-16 rounded-2xl ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-500 leading-relaxed mb-5">{service.description}</p>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm hover:gap-2.5 transition-all"
                >
                  Learn more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              The Process
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              How SOS SMS Works
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-xl mx-auto">
              Three simple steps between you and a trusted professional at your door.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-10 relative"
          >
            {/* Connector line */}
            <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

            {steps.map((step) => (
              <motion.div key={step.step} variants={fadeUp} className="text-center relative">
                <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary/20 flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-extrabold text-primary">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-500 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Platform Benefits
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Why Choose SOS SMS?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
              Built from the ground up to solve emergency home services - fast, reliable, and transparent.
            </motion.p>
          </motion.div>

                    <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="flex flex-wrap justify-center gap-6"
          >
            {features.map((f) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
              >
                <div className="text-3xl mb-4">{f.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

                  {/* ── PRICING ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-b from-gray-50/60 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Pricing
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              What it costs, with nothing buried
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="relative grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-0 items-stretch max-w-4xl mx-auto"
          >
            {/* Membership card */}
            <motion.div
              variants={fadeUp}
              className="bg-white rounded-2xl p-8 border border-gray-200 flex flex-col"
            >
              <div className="min-h-[176px]">
                <div className="w-11 h-11 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center mb-6">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>

                <div className="text-sm font-semibold uppercase tracking-wider text-primary mb-3">Membership</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-5xl font-extrabold text-gray-900 tracking-tight">$14</span>
                  <span className="text-base font-medium text-gray-500">/month</span>
                </div>
                <div className="text-sm text-gray-500">or $99/year ($8.25/month). Cancel anytime.</div>
              </div>

              <p className="text-sm text-gray-700 leading-relaxed mt-6 pt-6 border-t border-gray-100">
                Your membership buys the network, the response guarantee, and escrow protection on every job.
              </p>
            </motion.div>

            {/* Connector */}
            <motion.div variants={fadeUp} className="flex md:flex-col items-center justify-center gap-0 py-2 md:py-0 md:px-5">
              <div className="hidden md:block flex-1 w-px bg-gray-200" />
              <span className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-base font-semibold shrink-0 my-2 md:my-3">
                +
              </span>
              <div className="hidden md:block flex-1 w-px bg-gray-200" />
            </motion.div>

            {/* Trip charge card */}
            <motion.div
              variants={fadeUp}
              className="bg-white rounded-2xl p-8 border-2 border-primary/25 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_8px_24px_-8px_rgba(16,90,60,0.12)] flex flex-col"
            >
              <div className="min-h-[176px]">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
                  </svg>
                </div>

                <div className="text-sm font-semibold uppercase tracking-wider text-primary mb-3 leading-snug">
                  $150 trip charge, only when you actually need someone
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold text-gray-900 tracking-tight">$150</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100 space-y-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  When you send a request and we dispatch a contractor, $150 is held to get a licensed pro to your door.
                </p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  No request, no charge. Ever.
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-10 max-w-4xl mx-auto flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-6 py-4"
          >
            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <p className="text-sm text-gray-600 leading-relaxed">
              The work itself is between you and your contractor. Parts, labor, scope, price — agreed directly with the pro and billed directly by them. SOS SMS takes no percentage of the job.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA BANNER ────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-r from-green-800 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold mb-4">
              Are You a Service Professional?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-green-100 text-lg mb-8 max-w-xl mx-auto">
              Join SOS SMS's provider network and get matched with customers who need your skills - right now, in your area.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/provider/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-green-800 font-bold rounded-2xl hover:bg-green-50 transition-colors shadow-lg"
              >
                Join as a Provider
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/40 text-white font-semibold rounded-2xl hover:bg-white/10 transition-colors"
              >
                Learn More
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

     {/* ── WHY WE THINK THIS WORKS ──────────────────────────────────────────────── */}
<section className="py-20 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerContainer}
      className="text-center mb-14"
    >
      <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
        Why It Works
      </motion.p>
      <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
        Why we think this works
      </motion.h2>
      <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
        Two simple principles keep every job fair to you and to the contractor showing up.
      </motion.p>
    </motion.div>

    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerContainer}
      className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
    >
      {/* Card 1 */}
      <motion.div
        variants={fadeUp}
        className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
      >
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-9m9-4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">Contractors pay us nothing</h3>
        <p className="text-gray-500 leading-relaxed">
          No lead fees, no subscriptions. We have no incentive to send you to whoever paid the most — only to whoever can actually get to you.
        </p>
      </motion.div>

      {/* Card 2 */}
      <motion.div
        variants={fadeUp}
        className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
      >
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">The trip charge is secured before dispatch</h3>
        <p className="text-gray-500 leading-relaxed">
          The contractor knows they'll be paid for showing up. You know they're committed before they're on the way.
        </p>
      </motion.div>
    </motion.div>
  </div>
</section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              FAQ
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Frequently Asked Questions
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-3"
          >
            {faqs.map((faq) => (
              <motion.div key={faq.q} variants={fadeUp}>
                <FaqItem q={faq.q} a={faq.a} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl font-extrabold text-gray-900 mb-6">
              Ready to Get Started?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg mb-10 max-w-xl mx-auto">
              Sign up as a homeowner or a professional and experience the fastest home services dispatch platform available today.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/customer/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-green-700 transition-colors shadow-lg text-base"
              >
                I Need a Service
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/provider/signup"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary text-primary font-bold rounded-2xl hover:bg-green-50 transition-colors text-base"
              >
                I'm a Provider
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </MarketingLayout>
  )
}

export default HomePage
